import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Sparkles, ChevronRight, ChevronLeft, Check, Heart } from 'lucide-react';

const API_URL = window.location.origin;

const YummyDeclaration = ({ onComplete, isOnboarding = false }) => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [finalDeclaration, setFinalDeclaration] = useState('');

  useEffect(() => {
    fetchTemplate();
  }, []);

  const fetchTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/yummy-declaration/template`);
      setTemplate(response.data);
      
      // Pre-fill responses if they exist
      if (response.data.completed) {
        const existingResponses = {};
        response.data.sections.forEach(section => {
          if (section.user_response) {
            existingResponses[section.id] = section.user_response;
          }
        });
        setResponses(existingResponses);
        setFinalDeclaration(response.data.user_final_declaration || '');
      }
    } catch (error) {
      console.error('Failed to fetch YUMMY template:', error);
      toast.error('Failed to load declaration form');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!finalDeclaration.trim()) {
      toast.error('Please complete your final declaration');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/yummy-declaration/save`, {
        responses,
        final_declaration: finalDeclaration
      });
      toast.success('Your YUMMY Declaration has been saved! 💎');
      if (onComplete) onComplete();
    } catch (error) {
      toast.error('Failed to save declaration');
    } finally {
      setSaving(false);
    }
  };

  const totalSteps = template?.sections?.length + 1 || 6; // +1 for final declaration
  const progress = ((currentStep + 1) / totalSteps) * 100;
  const currentSection = template?.sections?.[currentStep];
  const isLastSection = currentStep === template?.sections?.length - 1;
  const isFinalStep = currentStep === template?.sections?.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin h-8 w-8 border-4 border-terracotta border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className={`${isOnboarding ? 'max-w-2xl mx-auto' : ''}`}>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className="h-16 w-16 rounded-full bg-gradient-to-br from-terracotta to-amber-500 flex items-center justify-center">
            <Sparkles className="h-8 w-8 text-white" />
          </div>
        </div>
        <h2 className="font-heading text-3xl text-forest mb-2">Your YUMMY Declaration</h2>
        <p className="text-gray-600">Your Ultimate Most Meaningful Yet</p>
      </div>

      {/* Progress */}
      <div className="mb-8">
        <Progress value={progress} className="h-2" />
        <p className="text-sm text-gray-500 text-center mt-2">
          Step {currentStep + 1} of {totalSteps}
        </p>
      </div>

      {/* Section Card */}
      {!isFinalStep && currentSection && (
        <Card className="mb-6 border-2" style={{ borderColor: currentSection.color + '40' }}>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3 mb-2">
              <div 
                className="h-12 w-12 rounded-full flex items-center justify-center text-white font-bold text-xl"
                style={{ backgroundColor: currentSection.color }}
              >
                {currentSection.letter}
              </div>
              <div>
                <CardTitle className="text-xl" style={{ color: currentSection.color }}>
                  {currentSection.word}
                </CardTitle>
                <CardDescription>{currentSection.pillar} Pillar</CardDescription>
              </div>
            </div>
            <p className="text-lg font-medium text-forest">{currentSection.theme}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700 leading-relaxed">{currentSection.prompt}</p>
            
            <p className="text-sm text-gray-500 italic">{currentSection.guidance}</p>
            
            <Textarea
              placeholder={currentSection.placeholder}
              value={responses[currentSection.id] || ''}
              onChange={(e) => setResponses(prev => ({
                ...prev,
                [currentSection.id]: e.target.value
              }))}
              className="min-h-[150px] text-base"
              data-testid={`yummy-response-${currentSection.id}`}
            />
          </CardContent>
        </Card>
      )}

      {/* Final Declaration */}
      {isFinalStep && (
        <Card className="mb-6 border-2 border-gold">
          <CardHeader className="pb-4 bg-gradient-to-r from-amber-50 to-yellow-50">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl text-amber-800">Your Complete Declaration</CardTitle>
                <CardDescription>Bring it all together</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            {/* Summary of responses */}
            <div className="space-y-3">
              {template?.sections?.map((section, idx) => (
                <div key={section.id} className="flex items-start gap-2">
                  <div 
                    className="h-6 w-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: section.color }}
                  >
                    {section.letter}
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">{section.word}:</span>
                    <p className="text-sm text-gray-600 italic">
                      {responses[section.id] ? `"${responses[section.id].substring(0, 100)}${responses[section.id].length > 100 ? '...' : ''}"` : '(not completed)'}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t pt-6">
              <p className="text-gray-700 mb-4">{template?.final_declaration?.prompt}</p>
              
              <div className="bg-cream p-4 rounded-lg mb-4">
                <p className="text-sm text-gray-600 italic">
                  {template?.final_declaration?.template}
                </p>
              </div>
              
              <Textarea
                placeholder={template?.final_declaration?.placeholder || "My YUMMY is..."}
                value={finalDeclaration}
                onChange={(e) => setFinalDeclaration(e.target.value)}
                className="min-h-[120px] text-base"
                data-testid="yummy-final-declaration"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex justify-between gap-4">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(prev => prev - 1)}
          disabled={currentStep === 0}
          className="flex-1"
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {!isFinalStep ? (
          <Button
            onClick={() => setCurrentStep(prev => prev + 1)}
            disabled={!responses[currentSection?.id]?.trim()}
            className="flex-1 btn-primary"
          >
            Continue
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            disabled={saving || !finalDeclaration.trim()}
            className="flex-1 btn-primary"
            data-testid="save-yummy-declaration"
          >
            {saving ? (
              <>Saving...</>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Save My Declaration
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default YummyDeclaration;
